import React, { forwardRef, ReactNode, useContext, useRef } from 'react'
import styles from './Field.module.sass'
import clsx from 'clsx'
import { Intent } from '../Intent'
import { RequestStatus } from '../../../responses/response'
import Skeleton from 'react-loading-skeleton'
import { useEffectOnce } from 'react-use'
import { statusContext } from '../../../constants/contexts'
import { Classes } from '../../../styles'

type BasicProps = {
    className?: string,
    value?: string | number | null,
    placeholder?: string,
    readonly?: boolean,
    autoFocus?: boolean,
    borderless?: boolean
    maxLength?: number,
    block?: boolean,
    intent?: Intent,
    leftItem?: ReactNode,
    rightItem?: ReactNode,
    onClick?: () => void,
    onEnter?: () => void,
    onEscape?: () => void,
    onCtrlEnter?: () => void,
    onFocus?: () => void,
    onBlur?: () => void,
    onUp?: () => void,
    onDown?: () => void,
}

export type ChangeProps = {
    type?: 'text' | 'password',
    onChange?: (value: string) => void,
} | {
    type: 'number',
    onChange?: (value: number) => void,
    min?: number,
    max?: number,
    step?: number,
}

export type FieldProps = BasicProps & ChangeProps

const Field = forwardRef<HTMLDivElement, FieldProps>((props, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useEffectOnce(() => {
        props.autoFocus && inputRef.current?.focus()
    })

    const status: RequestStatus = useContext(statusContext)

    if (status === RequestStatus.FETCHING) {
        return <div className={props.className} style={{ display: props.block ? 'block' : 'inline' }}>
            <Skeleton height={30}/>
        </div>
    }

    return <div
        ref={ref}
        className={clsx(props.className, styles.container, Classes.touchTarget, {[styles.block]: props.block})}
    >
        <div
            className={clsx(styles.field,styles[props.intent || Intent.NONE], {[styles.borderless]: props.borderless})}
            onClick={() => {
                props.onClick?.()
                inputRef.current?.focus({})
            }}
        >
            {
                props.leftItem && <div className={styles.leftItem}>{props.leftItem}</div>
            }
            <input
                ref={inputRef}
                className={styles.input}
                size={1}
                readOnly={props.readonly}
                type={props.type}
                value={props.value ?? ''}
                placeholder={props.placeholder}
                onFocus={props.onFocus}
                onBlur={() => setTimeout(() => props.onBlur?.(), 200)}
                min={props.type === 'number' ? props.min : undefined}
                max={props.type === 'number' ? props.max : undefined}
                step={props.type === 'number' ? props.step : undefined}
                maxLength={props.maxLength}
                onChange={event => {
                    if (props.type === 'number') {
                        props.onChange?.(parseFloat(event.currentTarget.value))
                    } else {
                        props.onChange?.(event.currentTarget.value)
                    }
                }}
                onKeyDown={event => {
                    switch (event.key) {
                        case 'Enter':
                            (event.ctrlKey ? props.onCtrlEnter : props.onEnter)?.()
                            break
                        case 'Escape':
                            props.onEscape?.()
                            break
                        case 'ArrowUp':
                            props.onUp?.()
                            break
                        case 'ArrowDown':
                            props.onDown?.()
                            break
                    }
                }}
            />
            {
                props.rightItem && <div className={styles.rightItem}>{props.rightItem}</div>
            }
        </div>
    </div>
})

Field.displayName = 'Field'

export default Field
